import type React from 'react';
import { Box, Card, FormField, FormHelp, Heading, Input, Label, Switch, TextButton } from '@zillow/constellation-10';
import { get } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { MAX_VEHICLE_FIELD_LENGTH } from '../../../../constants/form';
import { SettingCard } from './SettingCard';

export const VEHICLES_FIELD_ARRAY_NAME = 'vehicles.vehicles';

export function VehicleEdit({ index, onDelete }: { index: number; onDelete: () => void }) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const title = `Vehicle #${index + 1}`;
  const makeFieldName = `${VEHICLES_FIELD_ARRAY_NAME}[${index}].make`;
  const modelFieldName = `${VEHICLES_FIELD_ARRAY_NAME}[${index}].model`;
  const errorMake = get(errors, makeFieldName);
  const errorModel = get(errors, modelFieldName);

  return (
    <Card
      elevated={false}
      outlined
      aria-label={title}
      css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}
    >
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Heading level={4} css={{ textStyle: 'body-bold', color: 'text.subtle' }}>
          {title}
        </Heading>
        <TextButton onClick={onDelete} type="button" aria-label={`Delete ${title}`}>
          Delete
        </TextButton>
      </Box>
      <Box>
        <FormField
          label={<Label>Make</Label>}
          optional
          control={<Input maxLength={MAX_VEHICLE_FIELD_LENGTH} {...register(makeFieldName)} />}
          description={errorMake?.message ? <FormHelp>{String(errorMake.message)}</FormHelp> : undefined}
          error={!!errorMake}
        />
        <FormField
          label={<Label>Model</Label>}
          optional
          control={<Input maxLength={MAX_VEHICLE_FIELD_LENGTH} {...register(modelFieldName)} />}
          description={errorModel?.message ? <FormHelp>{String(errorModel.message)}</FormHelp> : undefined}
          error={!!errorModel}
        />
      </Box>
      <VehicleSwitch name={`${VEHICLES_FIELD_ARRAY_NAME}[${index}].isParkingSpotNeeded`} label="Parking spot needed" />
      <VehicleSwitch
        name={`${VEHICLES_FIELD_ARRAY_NAME}[${index}].isEvChargingNeeded`}
        label="EV charging needed"
        description="Electric or plug-in hybrid vehicle"
      />
    </Card>
  );
}

function VehicleSwitch({ name, label, description }: { name: string; label: string; description?: string }) {
  const labelId = `${name}-label`;
  return (
    <Controller
      name={name}
      render={({ field: { onChange, onBlur, value } }) => (
        <SettingCard label={label} labelId={labelId} description={description}>
          <Switch
            size="lg"
            name={name}
            aria-labelledby={labelId}
            checked={value === true}
            onBlur={onBlur}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
          />
        </SettingCard>
      )}
    />
  );
}
